import { useEffect, useState, useCallback } from "react";
import { documentsAPI } from "../services/documentsAPI";
import { useProjectContext } from "./useProjectContext";

export const useDocuments = () => {
  const { activeProject } = useProjectContext();
  const projectId = activeProject?.id;

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await documentsAPI.listByProject(projectId);
      setDocuments(res.data || []);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  const upload = async (fileOrFormData, metadata = {}, onProgress) => {
    if (!projectId) throw new Error("No active project selected");
    setUploading(true);
    setUploadProgress(null);
    setError(null);
    try {
      const isLegacyFormData =
        typeof FormData !== "undefined" && fileOrFormData instanceof FormData;
      const res = isLegacyFormData
        ? await documentsAPI.upload(projectId, fileOrFormData)
        : await documentsAPI.uploadDirect(
            projectId,
            fileOrFormData,
            metadata,
            (percent, phase) => {
              setUploadProgress(percent);
              onProgress?.(percent, phase);
            },
          );
      setDocuments((prev) => [res.data, ...prev]); // prepend newest doc
      return res.data;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const remove = async (documentId) => {
    if (!projectId) throw new Error("No active project selected");
    await documentsAPI.delete(projectId, documentId);
    setDocuments((prev) => prev.filter((d) => d.id !== documentId));
  };

  // Robust merge: supports either a full doc object (result.id)
  // or an indexing payload ({ document_id, chunks_indexed, project_id }).
  const mergeIndexResult = useCallback((result) => {
    const docId = result?.id ?? result?.document_id;
    if (!docId) return;

    setDocuments((prev) =>
      prev.map((d) => {
        if (d.id !== docId) return d;

        // If backend returned full doc, replace it
        if (result?.id) return result;

        // Otherwise patch locally with indexing info
        return {
          ...d,
          rag_index_status: "indexed",
          rag_chunks_indexed:
            typeof result?.chunks_indexed === "number"
              ? result.chunks_indexed
              : d.rag_chunks_indexed,
          rag_indexed_at: new Date().toISOString(),
        };
      }),
    );
  }, []);

  const indexForRag = async (documentId, options = {}) => {
    if (!projectId) throw new Error("No active project selected");
    setLoading(true);
    setError(null);
    try {
      const res = await documentsAPI.indexForRag(
        projectId,
        documentId,
        options,
      );
      mergeIndexResult(res.data);
      return res.data;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  // 7) Semantic index function (based on your actual hook)
  const indexForRagSemantic = async (documentId, formData) => {
    if (!projectId) throw new Error("No active project selected");
    setLoading(true);
    setError(null);
    try {
      const res = await documentsAPI.indexForRagSemantic(
        projectId,
        documentId,
        formData,
      );
      // response is RAGIndexResponse (not Document). Safest: refresh docs from backend so rag status updates
      await load();
      return res.data;
    } catch (e) {
      setError(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  return {
    documents,
    loading,
    uploading,
    uploadProgress,
    error,
    reload: load,
    upload,
    remove,
    indexForRag,
    indexForRagSemantic,
  };
};
